import { useGetPaymentMethods, useWarrantyData } from "app/hooks";
import { useOrderCreatePaymentLink } from "app/hooks/payment/useOrderCreatePaymentLink";
import { useStandardProductCreatePaymentLink } from "app/hooks/payment/useStandardProductCreatePaymentLink";
import { useGetStandardOrder } from "app/hooks/shared/standard-product/useGetStandardOrder";
import { useGetStandardOrderDeliveryPrice } from "app/hooks/shared/standard-product/useGetStandardOrderDeliveryPrice";
import { useOrderContext } from "app/providers/OrderProvider";
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryGates } from "app/hooks/shared/useQueryGates";
import {
  CreateOrderPaymentLinkPayload,
  CreateStandardOrderPaymentLinkPayload,
  GetOrderResponse,
  GetStandardProductOrderResponse,
  PaymentMethod as MiddlewarePaymentMethod,
  WarrantyPackage,
  WarrantyPackagesResponse,
} from "shared/api/middleware-api-client";
import {
  idealId,
  PaymentMethod,
  PaymentType,
  PaymentTypes,
  PaymentTypesCustom,
  PaymentTypesFull,
  PaymentTypesLaterMonths,
  PaymentTypesNow,
  paymentTypesSampleSale,
  warrantyOwnRiskAmount,
} from "shared/utils/payment";
import { PaymentActions } from "shared/utils/payment-methods";
import { maxMonthlyPrice, minMonthlyPrice } from "../../../shared/utils/price";
import { PaymentData } from "../types/payment";
import { setOrderWarrantyPackages } from "shared/store/voucher/voucherStore";

interface PaymentProviderProps {
  isPaid: () => boolean;
  onPaymentSubmit: () => void;
  customAmount: number;
  loaded: boolean;
  getPaymentTypeList: (paymentTypes: PaymentTypes[]) => PaymentType[];
  getPaymentListWithAssembly: (paymentTypes: PaymentTypes[]) => PaymentType[];
  bank: string;
  setBank: Dispatch<SetStateAction<string>>;
  paymentMethod: string;
  setPaymentMethod: Dispatch<SetStateAction<string>>;
  paymentAction: PaymentActions | null;
  setPaymentAction: Dispatch<SetStateAction<PaymentActions | null>>;
  canProceed: () => boolean;
  isCreatePaymentLinkLoading: boolean;
  isCreateStandardOrderPaymentLinkLoading: boolean;
  setFreeWarrantyPackage: (
    value: SetStateAction<WarrantyPackage | undefined>,
  ) => void;
  warrantyDescription: string;
  setWarrantyDescription: Dispatch<SetStateAction<string>>;
  setWarrantyPackages: Dispatch<SetStateAction<WarrantyPackage[]>>;
  warrantyPackages: WarrantyPackage[];
  setWarrantyTitle: Dispatch<SetStateAction<string>>;
  warrantyTitle: string;
  hasPaid: boolean;
  customPaymentAmount: number;
  paymentData: PaymentData | undefined;
  paymentStatus: PaymentStatus;
  doorOrder: GetOrderResponse | undefined;
  doorOrderError: any;
  standardProductOrder: GetStandardProductOrderResponse | undefined;
  orderType: OrderType | undefined;
  standardOrderDeliveryPrice: number;
}

export enum OrderType {
  STANDARD_PRODUCT = "p",
  DOOR = "d",
}

export enum PaymentStatus {
  FULLY_PAID,
  FULL_PAYMENT,
  PARTIAL_PAYMENT,
  CUSTOM_PAYMENT,
}

const PaymentContext = createContext({} as PaymentProviderProps);
export const usePaymentContext = () => useContext(PaymentContext);
const PaymentProvider = ({ children }: any) => {
  const [searchParams] = useSearchParams();
  const [paymentData, setPaymentData] = useState<PaymentData | undefined>(
    undefined,
  );
  const [orderType, setOrderType] = useState<OrderType | undefined>();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(
    PaymentStatus.FULL_PAYMENT,
  );
  const [doorOrder, setDoorOrder] = useState<GetOrderResponse | undefined>(
    undefined,
  );

  // ---------------------------------
  // Standard Order specific constants
  // const [standardProductOrder, setStandardProductOrder] = useState<
  // 	GetStandardProductOrderResponse | undefined
  // >(undefined);

  const orderID = searchParams.get("pOrderId") ?? "";
  const { data } = useGetStandardOrder(orderID);
  const standardProductOrder = data;
  const paymentDataStandardOrder = standardProductOrder?.paymentData;
  const { deliveryPriceAmount: standardOrderDeliveryPrice } =
    useGetStandardOrderDeliveryPrice(orderID);

  const {
    order,
    warrantyPackage,
    setWarrantyPackage,
    orderError: doorOrderError,
  } = useOrderContext();

  const { enableWarrantyOrPayment } = useQueryGates();

  const { warranties, warrantyPackagesDataRequest } = useWarrantyData(
    enableWarrantyOrPayment,
  );

  const {
    createOrderPaymentLinkToggle,
    isCreateOrderPaymentLinkLoading: isCreatePaymentLinkLoading,
  } = useOrderCreatePaymentLink();
  const {
    createStandardProductOrderPaymentLink,
    isCreateStandardOrderPaymentLinkLoading,
  } = useStandardProductCreatePaymentLink();

  const { paymentMethods } = useGetPaymentMethods(enableWarrantyOrPayment);

  const isWarrantyOwnRiskPayment: boolean = JSON.parse(
    searchParams.get("warranty") || "false",
  );
  const customPaymentAmount = parseInt(searchParams.get("amount") || "0");

  const [paymentMethod, setPaymentMethod] = useState<string>("");

  // const [paymentType, setPaymentType] = useState<>()
  const [bank, setBank] = useState<string>("");
  const [paymentAction, setPaymentAction] = useState<PaymentActions | null>(
    null,
  );

  const [hasPaid, setHasPaid] = useState(order?.paymentData.paid !== 0);
  const [customAmount, setCustomAmount] = useState<number>(0);

  const [freeWarrantyPackage, setFreeWarrantyPackage] =
    useState<WarrantyPackage>();

  const [warrantyTitle, setWarrantyTitle] = useState<string>("");
  const [warrantyDescription, setWarrantyDescription] = useState<string>("");

  const [warrantyPackages, setWarrantyPackages] = useState<WarrantyPackage[]>(
    [],
  );

  useEffect(() => {
    const orderId = searchParams.get("orderId") || "";
    const type = searchParams.get("type") || "";

    if (orderId && type === "") {
      retrievePaymentOrder();
      setOrderType(OrderType.DOOR);
    }
    const pOrderId = searchParams.get("pOrderId") || "";
    if (pOrderId || (orderId && type === "p")) {
      setOrderType(OrderType.STANDARD_PRODUCT);
    }
  }, [searchParams, order]);

  useEffect(() => {
    if (!paymentData) return;
    if (isPaid()) {
      setPaymentStatus(PaymentStatus.FULLY_PAID);
      return;
    }

    if (customPaymentAmount) {
      setPaymentStatus(PaymentStatus.CUSTOM_PAYMENT);
      return;
    }

    if (paymentData.paid > 0 || paymentData.supplyPaid > 0) {
      setPaymentStatus(PaymentStatus.PARTIAL_PAYMENT);
      return;
    }
    setPaymentStatus(PaymentStatus.FULL_PAYMENT);
  }, [paymentData, orderType]);

  useEffect(() => {
    if (
      orderType === OrderType.STANDARD_PRODUCT &&
      standardProductOrder?.paymentStatus === "fullyPaid"
    ) {
      setPaymentStatus(PaymentStatus.FULLY_PAID);
      return;
    }
  }, [standardProductOrder, orderType]);

  const loaded = Boolean(
    (paymentData || paymentDataStandardOrder) &&
      (order || standardProductOrder) &&
      paymentStatus !== null,
  );

  const retrievePaymentOrder = async () => {
    if (order) {
      setDoorOrder(order);
      setPaymentData(order.paymentData);
      if (order.warrantyOwnRiskPrice && isWarrantyOwnRiskPayment) {
        setPaymentAction(PaymentActions.PAY_CUSTOM);
        setCustomAmount(warrantyOwnRiskAmount);
      }
      setHasPaid(order?.paymentData.paid !== 0);
    }
  };

  // TODO: REFACTOR & Extract to warranty provider
  useEffect(() => {
    if (order?.warrantyPackage) return;
    let newWarrantyPackages: WarrantyPackage[] = [];
    const orderWarrantyPackages = warrantyPackagesDataRequest.data;
    if (orderWarrantyPackages && !order?.shoppingCart?.sampleSale) {
      let unPaidOrderWarrantyPresent = false;
      if (order?.warrantyPackage && !hasPaid) {
        unPaidOrderWarrantyPresent = true;
      }

      newWarrantyPackages = findNewWarrantyPackages(orderWarrantyPackages);

      if (unPaidOrderWarrantyPresent) {
        if (!order?.isSelfAssembly) {
          const warrantyPackageCode = order?.warrantyPackage?.code ?? "";

          if (warrantyPackageCode === "1jaar") {
            if (
              paymentAction === PaymentActions.PAY_NOW ||
              paymentAction === PaymentActions.PAY_LATER_MONTHS ||
              paymentAction === PaymentActions.PAY_LATER_TERMS
            ) {
              setWarrantyPackage(warranties.noWarrantyPayNowPackage);
            } else if (paymentAction === PaymentActions.PAY_FULL) {
              setWarrantyPackage(orderWarrantyPackages.freeWarrantyPackage);
            }
          } else {
            const correctWarrantyPackage = newWarrantyPackages.find((wp) => {
              return wp.code.includes(warrantyPackageCode.substring(0, 5));
            });
            setWarrantyPackage(correctWarrantyPackage);
          }
        }
      } else {
        if (!hasPaid) {
          if (
            paymentAction === PaymentActions.PAY_NOW ||
            paymentAction === PaymentActions.PAY_LATER_MONTHS ||
            paymentAction === PaymentActions.PAY_LATER_TERMS ||
            paymentAction === PaymentActions.PAY_CUSTOM ||
            paymentAction === null
          ) {
            setWarrantyTitle("Wil je extra zekerheid?");
            setWarrantyDescription(
              "Dan biedt GewoonGers de mogelijkheid om je bestelling uit te breiden met een zekerheidspakket. Hiermee ontvang je 3 óf 5 jaar extra zekerheid op onder andere glas en poedercoat. ",
            );

            newWarrantyPackages.push(warranties.noWarrantyPayNowPackage);
            newWarrantyPackages.push(warranties.decideLaterWarrantyPackage);

            if (
              !warrantyPackage ||
              warrantyPackage.packageId ===
                warranties.noWarrantyPayFullPackage.packageId
            ) {
              setWarrantyPackage(warranties.decideLaterWarrantyPackage);
            } else {
              if (
                warrantyPackage &&
                (warrantyPackage.code.includes("3jaar") ||
                  warrantyPackage.code.includes("5jaar"))
              ) {
                const sameWarrantyPackage = newWarrantyPackages.find((pkg) =>
                  warrantyPackage.code.includes(pkg.code),
                );
                setWarrantyPackage(sameWarrantyPackage);
              } else {
                const sameWarrantyPackage = newWarrantyPackages.find(
                  (pkg) => pkg.packageId === warrantyPackage.packageId,
                );
                setWarrantyPackage(sameWarrantyPackage);
              }
            }
          } else if (paymentAction === PaymentActions.PAY_FULL) {
            setWarrantyTitle("Wil je extra zekerheid?");
            setWarrantyDescription(
              "Dan biedt GewoonGers de mogelijkheid om je bestelling uit te breiden met een zekerheidspakket. Hiermee ontvang je 3 óf 5 jaar extra zekerheid op onder andere glas en poedercoat. ",
            );

            newWarrantyPackages.push(warranties.noWarrantyPayFullPackage);

            if (
              warrantyPackage &&
              (warrantyPackage.packageId ===
                warranties.decideLaterWarrantyPackage.packageId ||
                warrantyPackage.packageId ===
                  warranties.noWarrantyPayNowPackage.packageId)
            ) {
              setWarrantyPackage(warranties.noWarrantyPayFullPackage);
            } else {
              if (
                warrantyPackage &&
                (warrantyPackage.code === "3jaar" ||
                  warrantyPackage.code === "5jaar")
              ) {
                const sameWarrantyPackage = newWarrantyPackages.find((pkg) =>
                  pkg.code.includes(warrantyPackage.code),
                );
                setWarrantyPackage(sameWarrantyPackage);
              }
            }
          }
        } else if (paymentAction === PaymentActions.PAY_CUSTOM) {
          setWarrantyTitle("Jij hebt al extra zekerheid");
          setWarrantyDescription(
            `Je hebt gekozen voor het ${order?.warrantyPackage?.name} zekerheidspakket!`,
          );
          setWarrantyPackage(order?.warrantyPackage);

          newWarrantyPackages = [];
        } else {
          const noWarrantyPackage = hasPaid
            ? warranties.noWarrantyPayNowPackage
            : (orderWarrantyPackages.freeWarrantyPackage ??
              warranties.noWarrantyPayFullPackage);

          newWarrantyPackages.push(noWarrantyPackage);

          setWarrantyTitle("Wil je extra zekerheid?");
          setWarrantyDescription(
            "Dan biedt GewoonGers de mogelijkheid om je bestelling uit te breiden met een zekerheidspakket. Hiermee ontvang je 3 óf 5 jaar extra zekerheid op onder andere glas en poedercoat. ",
          );

          if (
            !warrantyPackage ||
            warrantyPackage.packageId === noWarrantyPackage.packageId
          ) {
            if (order?.warrantyPackage) {
              setWarrantyPackage(order.warrantyPackage);
            } else {
              setWarrantyPackage(warranties.noWarrantyPayNowPackage);
            }
          }
        }
      }
    }
    setWarrantyPackages(newWarrantyPackages);
    setOrderWarrantyPackages(newWarrantyPackages);
  }, [paymentAction, warrantyPackagesDataRequest.data, hasPaid]);

  // TODO: REFACTOR & extact to warranty provider
  useEffect(() => {
    if (!order?.warrantyPackage) {
      if (paymentAction !== PaymentActions.PAY_CUSTOM) {
        if (
          !warrantyPackage ||
          (warrantyPackage &&
            (warrantyPackage.packageId ===
              warranties.noWarrantyPayFullPackage.packageId ||
              warrantyPackage.packageId ===
                warranties.noWarrantyPayFullPackage.packageId) &&
            paymentAction === PaymentActions.PAY_NOW)
        ) {
          setWarrantyPackage(warranties.decideLaterWarrantyPackage);
        }
      }
    }
  }, [paymentAction]);

  useEffect(() => {
    if (order) {
      if (order && order.warrantyOwnRiskPrice && isWarrantyOwnRiskPayment) {
        setPaymentAction(PaymentActions.PAY_CUSTOM);
        setCustomAmount(warrantyOwnRiskAmount);
      }

      setHasPaid(order?.paymentData.paid !== 0);
    }
  }, [order]);

  useEffect(() => {
    if (customPaymentAmount && !isWarrantyOwnRiskPayment) {
      let trueCustomAmount = customPaymentAmount / 100;
      if (order) {
        const orderTotal = order?.paymentData.total; //+ order?.paymentData.supplyTotal;
        const orderPaid = order?.paymentData.paid; //+ order?.paymentData.supplyPaid;

        if (orderTotal - orderPaid < trueCustomAmount) {
          trueCustomAmount = orderTotal - orderPaid;
        }
      }
      setPaymentAction(PaymentActions.PAY_CUSTOM);
      setPaymentStatus(PaymentStatus.CUSTOM_PAYMENT);
      setCustomAmount(trueCustomAmount);
    }
  }, [order, customPaymentAmount]);

  // TODO: REFACTOR & extact to warranty provider
  const findNewWarrantyPackages = (
    warrantyPackagesResponse: WarrantyPackagesResponse,
  ) => {
    const newWarranties = [];
    if (
      paymentAction === null ||
      paymentAction === PaymentActions.PAY_NOW ||
      paymentAction === PaymentActions.PAY_LATER_MONTHS ||
      paymentAction === PaymentActions.PAY_LATER_TERMS ||
      paymentAction === PaymentActions.PAY_CUSTOM ||
      (paymentAction === PaymentActions.PAY_FULL && hasPaid)
    ) {
      newWarranties.push(...(warrantyPackagesResponse.payNowPackages ?? []));
    } else if (paymentAction === PaymentActions.PAY_FULL) {
      newWarranties.push(...(warrantyPackagesResponse.payFullPackages ?? []));
    }

    setFreeWarrantyPackage(warrantyPackagesResponse.freeWarrantyPackage);

    return newWarranties;
  };

  const isPaid = () => {
    if (orderType === OrderType.STANDARD_PRODUCT) {
      return standardProductOrder?.paymentStatus === "fullyPaid";
    }

    if (!paymentData) return false;
    return (
      paymentData.total +
        paymentData.supplyTotal -
        (paymentData.paid + paymentData.supplyPaid) ===
      0
    );
  };

  const canProceed = () => {
    if (orderType === OrderType.STANDARD_PRODUCT) {
      if (!paymentAction) return false;
      if (!paymentMethod) return false;
      if (
        (paymentAction === PaymentActions.PAY_FULL ||
          paymentAction === PaymentActions.PAY_NOW) &&
        !bank
      )
        return false;
      if (paymentMethod === idealId && !bank) return false;
      return true;
    } else if (OrderType.DOOR) {
      if (!order) return false;
      if (!paymentAction) return false;
      if (paymentAction === PaymentActions.BANK_TRANSFER) return true;
      if (!paymentMethod) return false;
      if (
        (paymentAction === PaymentActions.PAY_FULL ||
          paymentAction === PaymentActions.PAY_NOW ||
          paymentAction === PaymentActions.PAY_CUSTOM) &&
        !bank
      )
        return false;
      if (paymentMethod === idealId && !bank) return false;
      if (paymentAction === PaymentActions.PAY_CUSTOM) return true;
      if (!order.shoppingCart?.sampleSale && !warrantyPackage) return false;
      return true;
    } else {
      return false;
    }
  };

  const onPaymentSubmit = () => {
    if (!canProceed()) return;

    let paymentType = "";
    if (
      paymentAction === PaymentActions.PAY_FULL ||
      paymentAction === PaymentActions.PAY_LATER_MONTHS ||
      paymentAction === PaymentActions.PAY_CUSTOM
    )
      paymentType = "direct";
    else if (paymentAction === PaymentActions.PAY_NOW) paymentType = "deposit";

    if (orderType === OrderType.STANDARD_PRODUCT) {
      if (standardProductOrder) {
        // Create the payment link payload
        const paymentLinkPayload = {
          paymentType: paymentType,
          paymentMethod: parseInt(paymentMethod),
          paymentBank: parseInt(bank),
        } as CreateStandardOrderPaymentLinkPayload;

        // Check if the payment action is PAY_CUSTOM
        if (paymentAction === PaymentActions.PAY_CUSTOM) {
          // Convert the custom payment amount to euro
          const amountInEuro = customPaymentAmount * 0.01;
          // Set the payment amount in the payment link payload
          paymentLinkPayload.amount = amountInEuro;
        }

        // Create the standard product order payment link
        createStandardProductOrderPaymentLink({
          orderId: standardProductOrder.id,
          payload: paymentLinkPayload,
        });
      }
    } else {
      let payloadWarrantyPackage = undefined;

      const orderWarrantyPackages = warrantyPackagesDataRequest.data;

      // Return early if no order available
      if (!order) return;
      if (order?.warrantyPackage === undefined) {
        if (
          warrantyPackage?.packageId ===
          warranties.noWarrantyPayFullPackage.packageId
        ) {
          payloadWarrantyPackage = {
            packageId: orderWarrantyPackages?.freeWarrantyPackage?.packageId,
            price: orderWarrantyPackages?.freeWarrantyPackage?.price,
            duration: orderWarrantyPackages?.freeWarrantyPackage?.duration,
            exists: false,
          };
        } else {
          payloadWarrantyPackage =
            warrantyPackage?.packageId !==
              warranties.decideLaterWarrantyPackage.packageId &&
            warrantyPackage?.packageId !==
              warranties.noWarrantyPayNowPackage.packageId &&
            warrantyPackage?.packageId !==
              warranties.noWarrantyPayFullPackage.packageId
              ? {
                  packageId: warrantyPackage?.packageId,
                  price: warrantyPackage?.price,
                  duration: warrantyPackage?.duration,
                  exists: false,
                }
              : undefined;
        }
      } else {
        if (
          order.paymentData.paid === 0 &&
          order.warrantyPackage.packageId !== warrantyPackage?.packageId
        ) {
          if (
            warrantyPackage?.packageId !==
              warranties.decideLaterWarrantyPackage.packageId &&
            warrantyPackage?.packageId !==
              warranties.noWarrantyPayNowPackage.packageId &&
            warrantyPackage?.packageId !==
              warranties.noWarrantyPayFullPackage.packageId
          ) {
            payloadWarrantyPackage = {
              id: order.warrantyPackage.id,
              price: warrantyPackage?.price,
              packageId: warrantyPackage?.packageId,
              exists: true,
            };
          } else {
            payloadWarrantyPackage = {
              id: order.warrantyPackage.id,
              price: undefined,
              packageId: undefined,
              exists: true,
            };
          }
        }
      }
      // Create the payment link payload
      const paymentLinkPayload = {
        paymentType: paymentType,
        paymentMethod: parseInt(paymentMethod),
        paymentBank: parseInt(bank),
      } as CreateOrderPaymentLinkPayload;

      // Check if the payment action is PAY_CUSTOM
      if (paymentAction === PaymentActions.PAY_CUSTOM) {
        // Convert the custom payment amount to euro
        const amountInEuro = customPaymentAmount * 0.01;
        // Set the payment amount in the payment link payload
        paymentLinkPayload.amount = amountInEuro;
      }

      // Create the door order payment link
      createOrderPaymentLinkToggle({
        orderId: order.id,
        payload: paymentLinkPayload,
      });
    }
  };

  const getToBePaidAmount = () => {
    /**
     * useEffect checks if order total price doesn't exceed max price for SprayPay.
     * Price of orders with a warranty package, should always use warranty packages without discount, to avoid showing
     * SprayPay if order total is just under max monthly price with discount.
     */
    // let price = getOrderSubtotal();
    //
    // if (warrantyPackage) {
    //   const warrantyWithoutDiscount: WarrantyPackage | undefined =
    //     getOrderWarrantyPackages()?.payNowPackages?.find(
    //       (pkg) => pkg.code === warrantyPackage?.code.replace("100", "")
    //     );
    //
    //   if (warrantyWithoutDiscount) {
    //     price += warrantyWithoutDiscount.price;
    //   }
    // }
    if (customPaymentAmount > 0) {
      return customPaymentAmount / 100;
    }
    if (
      orderType === OrderType.STANDARD_PRODUCT &&
      standardProductOrder !== undefined
    ) {
      return standardProductOrder.payableAmount ?? 0;
    }
    if (orderType === OrderType.DOOR) {
      return (paymentData?.total ?? 0) - (paymentData?.paid ?? 0);
    }
    return 0;
  };

  const getPaymentTypesList = (): PaymentTypes[] => {
    switch (paymentStatus) {
      case PaymentStatus.FULL_PAYMENT:
        let newPaymentTypes: PaymentTypes[] = [];

        if (orderType === OrderType.DOOR && order?.shoppingCart?.sampleSale) {
          newPaymentTypes = paymentTypesSampleSale;
        } else if (orderType === OrderType.DOOR && order?.isSelfAssembly) {
          newPaymentTypes.push(PaymentTypesFull);
        } else {
          newPaymentTypes = [PaymentTypesFull];

          // A 10% deposit is available if there's no payment made and the order is a door
          if (orderType === OrderType.DOOR && paymentData?.paid === 0) {
            newPaymentTypes.push(PaymentTypesNow);
          }

          const toBePaidAmount = getToBePaidAmount();

          // If the amount is between min and max, and no payment is made, add the pay later months option
          if (
            toBePaidAmount > minMonthlyPrice &&
            toBePaidAmount < maxMonthlyPrice &&
            // orderType !== OrderType.DOOR &&
            (paymentData?.paid === 0 ||
              orderType === OrderType.STANDARD_PRODUCT)
          ) {
            newPaymentTypes.push(PaymentTypesLaterMonths);
          }
        }
        return newPaymentTypes;
      case PaymentStatus.PARTIAL_PAYMENT:
        return [PaymentTypesFull];
      case PaymentStatus.CUSTOM_PAYMENT:
        return [PaymentTypesCustom];
    }
    return [];
  };

  const getPaymentTypeList = (paymentTypes: PaymentTypes[]): PaymentType[] => {
    if (!paymentMethods) return [];

    const paymentTypesWithMethods: PaymentType[] = [];

    for (const paymentType of getPaymentTypesList()) {
      paymentTypesWithMethods.push({
        action: paymentType.action,
        methods: getPaymentMethodsForTypes(paymentType),
      });
    }

    return paymentTypesWithMethods;
  };

  const getPaymentListWithAssembly = (
    paymentTypes: PaymentTypes[],
  ): PaymentType[] => {
    if (!paymentMethods) return [];

    const paymentTypesWithMethods: PaymentType[] = [];

    for (const paymentType of paymentTypes) {
      paymentTypesWithMethods.push({
        action: paymentType.action,
        methods: getPaymentMethodsForTypes(paymentType),
      });
    }

    return paymentTypesWithMethods;
  };

  // SprayPay > 250 && SprayPay < 10.000
  // pay later = spraypay && !payments
  // pay now = 10 %
  // pay full = 100 % | 90 %

  const getPaymentMethodsForTypes = (paymentType: PaymentTypes) => {
    if (!paymentMethods) return [];

    const typePaymentMethods: PaymentMethod[] = [];
    for (const method of paymentType.methodIds) {
      const paymentMethod: MiddlewarePaymentMethod | undefined =
        paymentMethods.find((m: MiddlewarePaymentMethod) => m.id === method);
      if (paymentMethod) {
        typePaymentMethods.push({
          id: paymentMethod.id,
          name: paymentMethod.name,
          visibleName: paymentMethod.visibleName,
          maxAmount: parseFloat(paymentMethod.maxAmount),
          minAmount: parseFloat(paymentMethod.minAmount),
          countries: paymentMethod.countries,
          banks: paymentMethod.banks,
          brand: paymentMethod.brand,
        });
      }
    }

    return typePaymentMethods;
  };

  return (
    <PaymentContext.Provider
      value={{
        isPaid,
        onPaymentSubmit,
        customAmount,
        loaded,
        getPaymentTypeList,
        getPaymentListWithAssembly,
        bank,
        setBank,
        paymentMethod,
        setPaymentMethod,
        paymentAction,
        setPaymentAction,
        canProceed,
        isCreatePaymentLinkLoading,
        isCreateStandardOrderPaymentLinkLoading,
        setFreeWarrantyPackage,
        warrantyDescription,
        setWarrantyDescription,
        warrantyPackages,
        setWarrantyPackages,
        warrantyTitle,
        setWarrantyTitle,
        hasPaid,
        customPaymentAmount,
        paymentData,
        paymentStatus,
        doorOrder,
        doorOrderError,
        standardProductOrder,
        orderType,
        standardOrderDeliveryPrice,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};
export default PaymentProvider;
